import React from "react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Role Hierarchy Test Component
 * Demonstrates complete Laravel-equivalent role and permission system
 */
const RoleHierarchyTest = () => {
  const { 
    user, 
    hasPermission, 
    hasRoleLevel, 
    hasRoleOrHigher, 
    isAdminRole, 
    isManagementRole, 
    isStaffRole, 
    getUserRoleLevel, 
    getRoleDisplayName 
  } = useAuth();

  if (!user) {
    return (
      <div className="container-fluid">
        <div className="panel panel-default">
          <div className="panel-body">
            <h4>Role Hierarchy Test</h4>
            <p className="alert alert-warning">Please log in to test role hierarchy functionality.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="row">
        <div className="col-md-12">
          <h1>🔐 Role Hierarchy Test - Laravel Equivalent System</h1>
          <p className="text-muted">Complete demonstration of Laravel-compatible role and permission system</p>
        </div>
      </div>

      {/* User Information */}
      <div className="row">
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-header">
              <h3 className="panel-title">👤 Current User Information</h3>
            </div>
            <div className="panel-body">
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td><strong>Name:</strong></td>
                    <td>{user.first_name} {user.last_name}</td>
                  </tr>
                  <tr>
                    <td><strong>Email:</strong></td>
                    <td>{user.email}</td>
                  </tr>
                  <tr>
                    <td><strong>Primary Role:</strong></td>
                    <td>
                      <span className="badge badge-primary">
                        {user.role} ({getRoleDisplayName(user.role)})
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>All Roles:</strong></td>
                    <td>
                      {(user.roles || [user.role]).map(role => (
                        <span key={role} className="badge badge-info mr-2">
                          {role}
                        </span>
                      ))}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>User Role Level:</strong></td>
                    <td>
                      <span className="badge badge-success">
                        Level {getUserRoleLevel()}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Role Classification */}
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-header">
              <h3 className="panel-title">🎯 Role Classification</h3>
            </div>
            <div className="panel-body">
              <div className="role-classification">
                <div className={`alert ${isAdminRole() ? 'alert-success' : 'alert-danger'}`}>
                  <strong>Admin Role (Level 7+):</strong> {isAdminRole() ? 'YES ✅' : 'NO ❌'}
                </div>
                <div className={`alert ${isManagementRole() ? 'alert-success' : 'alert-danger'}`}>
                  <strong>Management Role (Level 5+):</strong> {isManagementRole() ? 'YES ✅' : 'NO ❌'}
                </div>
                <div className={`alert ${isStaffRole() ? 'alert-success' : 'alert-danger'}`}>
                  <strong>Staff Role (Level 3+):</strong> {isStaffRole() ? 'YES ✅' : 'NO ❌'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Role Level Tests */}
      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default">
            <div className="panel-header">
              <h3 className="panel-title">📊 Role Level Tests - Laravel Hierarchy</h3>
            </div>
            <div className="panel-body">
              <div className="row">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(level => (
                  <div key={level} className="col-md-2 mb-3">
                    <div className={`text-center p-3 border rounded ${
                      hasRoleLevel(level) ? 'bg-success text-white' : 'bg-danger text-white'
                    }`}>
                      <h4>Level {level}</h4>
                      <p>{hasRoleLevel(level) ? 'PASS ✅' : 'FAIL ❌'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Permission Tests */}
      <div className="row">
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-header">
              <h3 className="panel-title">🔑 Property Permission Tests</h3>
            </div>
            <div className="panel-body">
              {[
                'properties.show',
                'properties.edit', 
                'properties.create',
                'properties.delete',
                'properties.photos.manage',
                'properties.*'
              ].map(permission => (
                <div key={permission} className={`alert mb-2 ${
                  hasPermission(permission) ? 'alert-success' : 'alert-danger'
                }`}>
                  <strong>{permission}:</strong> {hasPermission(permission) ? 'GRANTED ✅' : 'DENIED ❌'}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Role Comparison Tests */}
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-header">
              <h3 className="panel-title">⚖️ Role Comparison Tests</h3>
            </div>
            <div className="panel-body">
              {[
                'CONTRACTOR',
                'GUARDIAN', 
                'CLIENT',
                'SALES',
                'GUARDIAN-MANAGER',
                'MAINTENANCE-FULL',
                'SENIOR-MANAGEMENT',
                'SYSTEM-ADMINISTRATOR'
              ].map(targetRole => (
                <div key={targetRole} className={`alert mb-2 ${
                  hasRoleOrHigher(targetRole) ? 'alert-success' : 'alert-danger'
                }`}>
                  <strong>{targetRole} or Higher:</strong> {hasRoleOrHigher(targetRole) ? 'YES ✅' : 'NO ❌'}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Implementation Status */}
      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-success">
            <div className="panel-header">
              <h3 className="panel-title">🎉 Laravel Parity Status</h3>
            </div>
            <div className="panel-body">
              <div className="row">
                <div className="col-md-3">
                  <h4>✅ Role Configuration</h4>
                  <p>16 Laravel roles implemented with exact hierarchy levels</p>
                </div>
                <div className="col-md-3">
                  <h4>✅ Permission System</h4>
                  <p>Laravel-equivalent permission checking with wildcard support</p>
                </div>
                <div className="col-md-3">
                  <h4>✅ Role Hierarchy</h4>
                  <p>Level-based access control with inheritance</p>
                </div>
                <div className="col-md-3">
                  <h4>✅ Backend Integration</h4>
                  <p>Complete middleware system with role fallback</p>
                </div>
              </div>
              <div className="text-center mt-3">
                <h3 className="text-success">🏆 Complete Laravel Parity Achieved!</h3>
                <p className="lead">Role-based and permission-based systems fully implemented</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleHierarchyTest;







